/**
 * ActiveStateCommand
 *
 * Captures before/after active (enabled) state for a SceneObject.
 */

import type { UndoCommand, SerializedCommand } from './UndoCommand';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import { registerCommandCodec } from './CommandCodec';

const CODEC_TYPE = 'ActiveState';

interface ActiveStatePayload {
    objectPath: string;
    before: boolean;
    after: boolean;
}

export class ActiveStateCommand implements UndoCommand {
    readonly label: string;
    /** Shared identity ref — survives Create/Delete undo cycles. */
    private identity: SceneObjectRef;
    private before: boolean;
    private after: boolean;
    private pathAtConstruction: string;

    constructor(sceneObject: SceneObject, before: boolean, after: boolean) {
        this.identity = sceneObject.traceScene.objectIdentityRegistry.refFor(sceneObject);
        this.before = before;
        this.after = after;
        this.pathAtConstruction = sceneObject.getPath();
        this.label = after ? `Enable ${sceneObject.name}` : `Disable ${sceneObject.name}`;
    }

    undo(): void {
        this.applyState(this.before);
    }

    redo(): void {
        this.applyState(this.after);
    }

    private applyState(active: boolean): void {
        const obj = this.identity.get();
        if (!obj) return;
        obj.setActive(active);
        obj.traceScene?.select(obj);
    }

    serialize(): SerializedCommand {
        const obj = this.identity.get();
        const payload: ActiveStatePayload = {
            objectPath: obj ? obj.getPath() : this.pathAtConstruction,
            before: this.before,
            after: this.after,
        };
        return { type: CODEC_TYPE, data: payload };
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): ActiveStateCommand => {
    const p = data as ActiveStatePayload;
    const obj = scene.resolveSceneObject(p.objectPath);
    if (!obj) {
        throw new Error(`[ActiveStateCommand] Cannot resolve path "${p.objectPath}"`);
    }
    return new ActiveStateCommand(obj, p.before, p.after);
});
